import { ReactNode } from "react";
import { useDroppable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";

type Props = {
  title: string;
  count?: number;
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadMoreDisabled?: boolean;
  // Optional Tailwind classes for a thin colored bar on the column header
  // (used by Inactivos to encode temperature visually).
  accentBarClass?: string;
  // Optional dnd-kit droppable id. When set, the column registers itself as a
  // drop target. `droppableDisabled` is checked at the time of the dragOver
  // event by the parent — we still mount the droppable so the visual treatment
  // is symmetric, but when disabled the parent can also skip rendering it.
  droppableId?: string;
  droppableDisabled?: boolean;
  // Optional content rendered below the title row (e.g. multi-select controls).
  headerAction?: ReactNode;
  children: ReactNode;
};

export const KanbanColumn = ({
  title,
  count,
  isLoading,
  hasMore,
  onLoadMore,
  loadMoreDisabled,
  accentBarClass,
  droppableId,
  droppableDisabled,
  headerAction,
  children,
}: Props) => {
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId ?? `column:${title}`,
    disabled: !droppableId || droppableDisabled,
  });

  return (
    <div
      ref={droppableId ? setNodeRef : undefined}
      className={cn(
        "flex w-72 min-h-0 shrink-0 flex-col rounded-lg border border-gray-200 bg-gray-50",
        "max-h-full transition-colors",
        isOver && !droppableDisabled ? "ring-2 ring-palomma-primary/50" : ""
      )}
    >
      {accentBarClass ? <div className={cn("h-1 rounded-t-lg", accentBarClass)} /> : null}
      <div
        className={cn(
          "sticky top-0 z-[1] flex items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2",
          accentBarClass ? "" : "rounded-t-lg"
        )}
      >
        <span className="truncate text-sm font-semibold text-gray-900">{title}</span>
        {typeof count === "number" ? (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {count}
          </span>
        ) : null}
      </div>
      {headerAction}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
        {isLoading ? (
          <span className="px-1 py-2 text-xs text-gray-400">Cargando…</span>
        ) : (
          children
        )}
        {hasMore && onLoadMore ? (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadMoreDisabled}
            className={cn(
              "mt-1 rounded-md border border-dashed border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600",
              "transition-colors hover:border-palomma-primary/50 hover:text-palomma-primary",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            {loadMoreDisabled ? "Cargando…" : "Cargar más"}
          </button>
        ) : null}
      </div>
    </div>
  );
};
